import React, { useState } from "react";
import Box from "@mui/material/Box";
import { useFormik } from "formik";
import { object, ref, string } from "yup";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import { useDispatch } from "react-redux";
import { Navigate } from "react-router-dom";
import { Input } from "../input/Input";
import {
  changePassword,
  confirmPasswordLabel,
  confirmPasswordValidate,
  currentPasswordLabel,
  enterConfirmPasswordPlaceHolder,
  enterCurrentPasswordPlaceHolder,
  enterNewPasswordPlaceHolder,
  newPasswordLabel,
  passwordPolicyMessage,
  passwordSuccess,
  submit,
} from "../messages";
import {
  callAPIInterface,
  char8,
  lowerCase,
  number,
  passwordPolicy,
  passwordPolicyState,
  upperCase,
} from "../constants";
import Text from "../text/Text";
import InputErrorMessage from "../inputErrorMessage/InputErrorMessage";
import Button from "../button/Button";
import CustomDivider from "../divider/Divider";
import { showToast } from "../../redux/actions";
import { useSession } from "../../hooks/useSession";
import CommonHeader from "../CommonHeader";

const ChangePassword = () => {
  const dispatch = useDispatch();
  const [activeState, setActiveState] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const { session } = useSession();

  const validationSchema = object({
    old_password: string().required(""),
    new_password: string()
      .matches(char8)
      .matches(upperCase)
      .matches(lowerCase)
      .matches(number)
      .required(""),
    confirm_password: string()
      .oneOf([ref("new_password"), null], confirmPasswordValidate)
      .required(""),
  });

  const initialValues = {
    old_password: "",
    new_password: "",
    confirm_password: "",
  };

  const formik = useFormik({
    initialValues: initialValues,
    validationSchema: validationSchema,
    onSubmit: async (val, { resetForm }) => {
      setIsSubmitting(true);
      try {
        await callAPIInterface(
          "POST",
          "/change-password",
          JSON.stringify({
            old_password: val?.old_password,
            new_password: val?.new_password,
          })
        );
        dispatch(
          showToast({
            isToastOpen: true,
            toastMessage: passwordSuccess,
            toastVariant: "success",
          })
        );
        resetForm(initialValues);
        setActiveState([]);
      } catch (error) {
      } finally {
        setIsSubmitting(false);
      }
    },
  });

  const {
    values,
    touched,
    errors,
    isValid,
    dirty,
    handleChange,
    handleSubmit,
    handleBlur,
  } = formik;

  const defaultErrorMessageProps = {
    touched,
    errors,
  };

  if (session && session?.user?.signup_method !== "email") {
    return <Navigate to="/settings" />;
  }

  return (
    <>
      <CommonHeader textAfterIcon={changePassword} icon="backArrow" />
      <Box className="options-container">
        <Box className="apply-promo-code-wrapper">
          <Box
            className="module-container"
            display="flex"
            flexDirection="column"
            gap={2}
          >
            <Box className="module-container-content">
              <Input
                fullWidth
                type="password"
                isPasswordVisible={true}
                showLabel
                label={currentPasswordLabel}
                placeholder={enterCurrentPasswordPlaceHolder}
                name="old_password"
                value={values.old_password}
                onChange={handleChange}
                error={touched.old_password && Boolean(errors.old_password)}
              />
              <Input
                customClass="mt-24"
                fullWidth
                type="password"
                isPasswordVisible={true}
                showLabel
                label={newPasswordLabel}
                placeholder={enterNewPasswordPlaceHolder}
                name="new_password"
                value={values.new_password}
                onChange={(e) => {
                  handleChange(e);
                  setActiveState(passwordPolicyState(e.target.value));
                }}
                error={touched.new_password && Boolean(errors.new_password)}
              />
              <Box className="mt-8 password-guide">
                <Text className="grey-text" size={12} component="span">
                  {passwordPolicyMessage}
                </Text>
                <Box
                  component="span"
                  width="fit-content"
                  className="password-guide-sub-info"
                >
                  {passwordPolicy.map((item, index) => {
                    const lastIndex = passwordPolicy.length - 1;
                    const styles = {
                      color: activeState.includes(item) ? "#0CAD62" : "#FD371E",
                    };
                    return (
                      <Box
                        component="span"
                        display="flex"
                        justifyContent="center"
                        alignItems="center"
                        key={item}
                        gap={0.25}
                      >
                        <Text size={12} style={styles} component="span">
                          {item}
                        </Text>
                        <CheckCircleIcon
                          sx={{ width: "12px", height: "12px", ...styles }}
                          className="info-popper-icon"
                        />
                        {index !== lastIndex && (
                          <Text
                            size={12}
                            className="grey-text"
                            component="span"
                          >
                            |
                          </Text>
                        )}
                      </Box>
                    );
                  })}
                </Box>
              </Box>
              <Input
                fullWidth
                customClass="mt-24"
                type="password"
                isPasswordVisible={true}
                showLabel
                label={confirmPasswordLabel}
                placeholder={enterConfirmPasswordPlaceHolder}
                name="confirm_password"
                value={values.confirm_password}
                onChange={handleChange}
                onBlur={handleBlur}
                error={
                  touched.confirm_password && Boolean(errors.confirm_password)
                }
              />
              <InputErrorMessage
                {...defaultErrorMessageProps}
                inputName="confirm_password"
              />
            </Box>
            <Box className="module-container-footer">
              <CustomDivider className="settings-divider" />

              <Button
                fullWidth
                variant="contained"
                label={submit}
                disabled={!(isValid && dirty) || isSubmitting}
                onClick={handleSubmit}
                className="settings-button"
              />
            </Box>
          </Box>
        </Box>
      </Box>
    </>
  );
};

export default ChangePassword;
